import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hasPermission, getToken } from '../services/authService';
import API_BASE_URL from '../config';
import toast from 'react-hot-toast';
import { getCategories } from '../services/categoryService';
import { priceInfo } from '../utils/priceUtils';

export default function AdminOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editStatus, setEditStatus] = useState('');
  const [editNote, setEditNote] = useState('');
  const [historyUser, setHistoryUser] = useState(null);
  const [historyOrders, setHistoryOrders] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchCriteria, setSearchCriteria] = useState('customerName');
  const [selectedDate, setSelectedDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [categoryTree, setCategoryTree] = useState([]);
  const itemsPerPage = 15;

  useEffect(() => {
    getCategories().then(res => setCategoryTree(res || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!hasPermission('admin.orders')) {
      navigate('/login');
      return;
    }
    let mounted = true;
    async function load() {
      setLoading(true);
      try {
        const token = getToken();
        // Must ensure we select currency/exchangeRate if backend doesn't send * by default.
        // Assuming backed sends all fields.
        const res = await fetch(`${API_BASE_URL}/orders`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        const data = await res.json();
        if (!mounted) return;
        const filtered = Array.isArray(data)
          ? data.filter(o => String(o.status || '').toLowerCase() !== 'cart')
          : [];
        setOrders(filtered);
      } catch (err) {
        console.error(err);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [navigate]);

  async function handleStatusChange(id, newStatus, newNote) {
    try {
      const token = getToken();
      const bodyPayload = { status: newStatus };
      if (newNote !== undefined) bodyPayload.adminNote = newNote;

      const res = await fetch(`${API_BASE_URL}/orders/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(bodyPayload)
      });
      if (!res.ok) throw new Error('Failed to update status');

      // Optimistic or refresh
      setOrders(prev => prev.map(o => o.id === id ? { ...o, status: newStatus, adminNote: newNote !== undefined ? newNote : o.adminNote } : o));
      if (selectedOrder && selectedOrder.id === id) {
        setSelectedOrder(prev => ({ ...prev, status: newStatus, adminNote: newNote !== undefined ? newNote : prev.adminNote }));
      }
      toast.success('Përditësimi u krye me sukses');
    } catch (err) {
      console.error(err);
      toast.error('Gabim gjatë përditësimit të statusit: ' + err.message);
      throw new Error('Failed to update status');
    }
  }

  const openOrder = (order) => {
    setSelectedOrder(order);
    setEditStatus(order.status || 'pending');
    setEditNote(order.adminNote || '');
  };

  const closeOrder = () => {
    setSelectedOrder(null);
  };

  const openHistory = async () => {
    if (!selectedOrder || !selectedOrder.email) return;
    setHistoryUser(selectedOrder);
    setHistoryLoading(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/orders/history?email=${encodeURIComponent(selectedOrder.email)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const data = await res.json();
      setHistoryOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setHistoryOrders([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistory = () => {
    setHistoryUser(null);
    setHistoryOrders([]);
    setHistoryLoading(false);
  };

  const saveStatusInModal = async () => {
    if (!selectedOrder) return;
    await handleStatusChange(selectedOrder.id, editStatus, editNote);
    closeOrder();
  };

  const getOldProductFromOrder = (order) => {
    if (!order) return null;
    if (order.oldProduct) return order.oldProduct;
    const searchStr = `${order.address || ''} ${order.adminNote || ''} ${order.company || ''}`;
    if (!searchStr) return null;
    const match = searchStr.match(/(?:\[?Produkti i vjetë?r:?\s*|\[?Old Product:?\s*)([^\]\n|]+)\]?/i);
    if (match && match[1]) return match[1].trim();
    return null;
  };

  const getOtnDiscountFromOrder = (order) => {
    if (!order) return 0;
    const searchStr = `${order.address || ''} ${order.adminNote || ''}`;
    const match = searchStr.match(/\[OTN-ZBRITJE:\s*(\d+(?:\.\d+)?)%/i);
    if (match && match[1]) return Number(match[1]);
    return 0;
  };

  const getOtnCategoryFromOrder = (order) => {
    if (!order) return '';
    const searchStr = `${order.address || ''} ${order.adminNote || ''}`;
    const match = searchStr.match(/\[OTN-ZBRITJE:\s*\d+(?:\.\d+)?%\|CAT:\s*([^\]]+)\]/i);
    if (match && match[1]) return match[1].trim();
    return '';
  };

  const getCleanAddress = (addr) => {
    if (!addr) return '—';
    const cleaned = addr
      .replace(/\|\s*\[Produkti i vjetë?r:?[^\]]*\]/gi, '')
      .replace(/\|\s*\[OTN-ZBRITJE:[^\]]*\]/gi, '')
      .replace(/\[Produkti i vjetë?r:?[^\]]*\]/gi, '')
      .replace(/\[OTN-ZBRITJE:[^\]]*\]/gi, '')
      .trim();
    return cleaned || '—';
  };

  function getTopCategoryId(catId, tree) {
    if (!catId || !tree || !tree.length) return String(catId || '');
    function findPath(list, id, path = []) {
      for (const n of list) {
        const currentPath = [...path, n];
        if (String(n.id) === String(id) || String(n.name).toLowerCase() === String(id).toLowerCase()) return currentPath;
        if (n.subcategories && n.subcategories.length) {
          const found = findPath(n.subcategories, id, currentPath);
          if (found) return found;
        }
      }
      return null;
    }
    const p = findPath(tree, catId);
    return (p && p.length) ? String(p[0].id) : String(catId);
  }

  const getItemDiscountPct = (item, order) => {
    if (item && item.otnDiscount !== undefined && item.otnDiscount !== null) {
      return Number(item.otnDiscount || 0);
    }
    if (!order) return 0;
    const isOTNOrder = Boolean(getOldProductFromOrder(order));
    const storedPct = getOtnDiscountFromOrder(order);
    const storedCatId = getOtnCategoryFromOrder(order);
    if (!isOTNOrder || storedPct <= 0 || !storedCatId) return 0;

    const catRef = item.product ? (item.product.categoryId || item.product.category) : null;
    const topId = catRef ? getTopCategoryId(catRef, categoryTree) : null;
    if (String(topId) === String(storedCatId) || String(catRef) === String(storedCatId)) {
      return storedPct;
    }
    return 0;
  };

  const getItemBasePrice = (item) => {
    if (!item) return 0;
    const servicesSum = Array.isArray(item.services)
      ? item.services.reduce((ss, sv) => ss + Number((sv && sv.price) || 0), 0)
      : 0;

    if (item.productPrice !== undefined && item.productPrice !== null && Number(item.productPrice) > 0) {
      return Number(item.productPrice) + servicesSum;
    }

    if (item.product) {
      const info = priceInfo(item.product);
      if (info && info.isOffer && info.display > 0) {
        return info.display + servicesSum;
      }
    }

    return Number(item.price || 0);
  };

  const getItemOriginalPrice = (item) => {
    if (!item) return null;
    const servicesSum = Array.isArray(item.services)
      ? item.services.reduce((ss, sv) => ss + Number((sv && sv.price) || 0), 0)
      : 0;

    if (item.originalPrice !== undefined && item.originalPrice !== null && Number(item.originalPrice) > 0) {
      return Number(item.originalPrice);
    }

    if (item.product && item.product.price) {
      const orig = Number(item.product.price) + servicesSum;
      const base = getItemBasePrice(item);
      if (orig > base) return orig;
    }

    return null;
  };

  const formatOrderPrice = (valInAll, order) => {
    const num = Number(valInAll || 0);
    const code = order.currency || 'ALL';
    const rate = Number(order.exchangeRate || 1);

    if (code === 'ALL') {
      return `${Math.round(num).toLocaleString('en-US')} L`;
    }

    const converted = num / rate;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: converted % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    }).format(converted);
  };
  const filteredOrders = orders.filter(o => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();

    switch (searchCriteria) {
      case 'id':
        return String(o.id).toLowerCase().includes(term);
      case 'productName':
        return (o.items || []).some(it =>
          (it.product?.name || it.productName || '').toLowerCase().includes(term)
        );
      case 'customerName':
        const fullName = `${o.firstName || ''} ${o.lastName || ''}`.toLowerCase();
        return fullName.includes(term);
      case 'email':
        return (o.email || '').toLowerCase().includes(term);
      case 'status':
        return (o.status || '').toLowerCase().includes(term);
      default:
        return true;
    }
  });

  const finalFilteredOrders = filteredOrders.filter(o => {
    if (!selectedDate) return true;
    const orderDate = new Date(o.createdAt).toISOString().split('T')[0];
    return orderDate === selectedDate;
  });

  // Reset to page 1 when search or date changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, searchCriteria, selectedDate]);

  const totalPages = Math.ceil(finalFilteredOrders.length / itemsPerPage);
  const paginatedOrders = finalFilteredOrders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) return <div style={{ padding: 20 }}>Loading orders...</div>;

  const statusOptions = ['pending', 'processing', 'delivered', 'returned', 'cancelled', 'awaiting_payment', 'paid'];

  return (
    <div>

      <div className="admin-card">
        <div style={{ marginBottom: 20, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', padding: '8px 12px', borderRadius: 10, border: '1px solid #e2e8f0', flex: 1, minWidth: '250px' }}>
            <span style={{ color: '#64748b' }}>🔍</span>
            <input
              type="text"
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ border: 'none', background: 'transparent', outline: 'none', width: '100%', fontSize: 14, color: '#0f172a' }}
            />
          </div>
          <select
            className="admin-select"
            value={searchCriteria}
            onChange={(e) => setSearchCriteria(e.target.value)}
            style={{ width: 'auto', minWidth: 160 }}
          >
            <option value="customerName">Customer Name</option>
            <option value="id">Order ID</option>
            <option value="productName">Product Name</option>
            <option value="email">Email</option>
            <option value="status">Status</option>
          </select>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', padding: '8px 12px', borderRadius: 10, border: '1px solid #e2e8f0', minWidth: '180px' }}>
            <span style={{ color: '#64748b' }}>📅</span>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              style={{ border: 'none', background: 'transparent', outline: 'none', width: '100%', fontSize: 13, color: '#0f172a' }}
            />
          </div>

          {/* <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
            {finalFilteredOrders.length} results
          </div> */}
        </div>

        {selectedDate && (
          <div style={{ marginBottom: 15, display: 'flex' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#eef2ff', color: '#4338ca', padding: '4px 10px', borderRadius: 16, fontSize: 12, fontWeight: 600 }}>
              <span>Date: {selectedDate}</span>
              <button
                onClick={() => setSelectedDate('')}
                style={{ border: 'none', background: 'transparent', color: '#4338ca', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', fontSize: 14 }}
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {finalFilteredOrders.length === 0 ? <div className="admin-text">No orders matched your search</div> : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Address</th>
                  <th>Status</th>
                  <th>Method</th>
                  <th>Total</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {paginatedOrders.map(o => (
                  <tr key={o.id} onClick={() => openOrder(o)} style={{ cursor: 'pointer' }} className="admin-order-row">
                    <td>{o.id}</td>
                    <td>
                      {o.firstName} {o.lastName}
                      {((o.items && o.items.some(it => it.hasTradeIn)) || (o.address && o.address.includes('Sill të vjetrën')) || (o.adminNote && o.adminNote.includes('Sill të vjetrën'))) && (
                        <span style={{ display: 'inline-block', marginLeft: '6px', background: '#dcfce7', color: '#166534', fontSize: '11px', fontWeight: '700', padding: '2px 7px', borderRadius: '12px', border: '1px solid #86efac' }} title="Klienti dorëzon pajisjen e vjetër gjatë marrjes/dërgesës">
                          ♻️ Me Pajisje të Vjetër
                        </span>
                      )}
                      {getOldProductFromOrder(o) && !((o.items && o.items.some(it => it.hasTradeIn))) && (
                        <span style={{ display: 'inline-block', marginLeft: '6px', background: '#ffedd5', color: '#c2410c', fontSize: '11px', fontWeight: '700', padding: '2px 7px', borderRadius: '12px', border: '1px solid #fed7aa' }} title={`Produkti i vjetër: ${getOldProductFromOrder(o)}`}>
                          ♻️ {getOldProductFromOrder(o)}
                        </span>
                      )}
                    </td>
                    <td>{o.email}</td>
                    <td>{o.pickupMethod === 'store' ? 'Tërheqje në Dyqan' : `${o.city || ''}, ${getCleanAddress(o.address)}`}</td>
                    <td>
                      <span className={`status-badge status-${String(o.status).toLowerCase()}`}>
                        {o.status}
                      </span>
                    </td>
                    <td>{o.paymentMethod} / {o.pickupMethod}</td>
                    <td>{formatOrderPrice(o.total, o)}</td>
                    <td>{new Date(o.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div style={{ marginTop: 20, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10 }}>
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => p - 1)}
              style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}
            >
              Previous
            </button>
            <div style={{ fontSize: 14, color: '#64748b', fontWeight: 600 }}>
              Page {currentPage} of {totalPages}
            </div>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(p => p + 1)}
              style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1 }}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      {selectedOrder && (
        <div className="admin-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) closeOrder(); }}>
          <div className="admin-modal-container" onClick={e => e.stopPropagation()}>

            <div className="admin-modal-header">
              <h2 className="admin-modal-title">Order #{selectedOrder.id}</h2>
              <button className="admin-modal-close" onClick={closeOrder}>✕</button>
            </div>

            <div className="admin-modal-body">
              {((selectedOrder.items && selectedOrder.items.some(it => it.hasTradeIn)) || (selectedOrder.adminNote && selectedOrder.adminNote.includes('Sill të vjetrën')) || (selectedOrder.address && selectedOrder.address.includes('Sill të vjetrën'))) && (
                <div style={{ background: '#f0fdf4', border: '2px solid #22c55e', padding: '14px 16px', borderRadius: '10px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '24px' }}>♻️</span>
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 800, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                        Fushata "Sill të vjetrën, merr të renë"
                      </div>
                      <div style={{ fontSize: '15px', fontWeight: 800, color: '#166534', marginTop: '2px' }}>
                        Klienti ka zgjedhur të dorëzojë pajisjen e vjetër gjatë marrjes në dyqan ose dërgesës në adresë.
                      </div>
                    </div>
                  </div>
                  <span style={{ background: '#16a34a', color: '#ffffff', fontSize: '12px', fontWeight: 700, padding: '4px 10px', borderRadius: '20px' }}>
                    ♻️ Dorëzim Pajisjeje
                  </span>
                </div>
              )}
              {getOldProductFromOrder(selectedOrder) && !(selectedOrder.items && selectedOrder.items.some(it => it.hasTradeIn)) && (
                <div style={{ background: '#fff7ed', border: '2px solid #f97316', padding: '14px 16px', borderRadius: '10px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '24px' }}>♻️</span>
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 800, color: '#c2410c', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                        Oferta "Nga e Vjetra në të Re" (Old to New Offer)
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: 800, color: '#9a3412', marginTop: '2px' }}>
                        Produkti i dhënë për ndërrim: {getOldProductFromOrder(selectedOrder)}
                      </div>
                    </div>
                  </div>
                  <span style={{ background: '#f97316', color: '#ffffff', fontSize: '12px', fontWeight: 700, padding: '4px 10px', borderRadius: '20px' }}>
                    Aktiv
                  </span>
                </div>
              )}

              <div className="admin-modal-grid">
                <div className="admin-detail-card">
                  <div className="admin-modal-section-title">Customer Details</div>
                  <div className="admin-detail-row">
                    <span className="admin-detail-label">Name</span>
                    <span className="admin-detail-value">{selectedOrder.firstName} {selectedOrder.lastName}</span>
                  </div>
                  <div className="admin-detail-row">
                    <span className="admin-detail-label">Email</span>
                    <span className="admin-detail-value">{selectedOrder.email}</span>
                  </div>
                  <div className="admin-detail-row">
                    <span className="admin-detail-label">Phone</span>
                    <span className="admin-detail-value">{selectedOrder.phone}</span>
                  </div>
                  <div className="admin-detail-row">
                    <span className="admin-detail-label">Company</span>
                    <span className="admin-detail-value">{selectedOrder.company || '—'}</span>
                  </div>
                  <div className="admin-detail-row">
                    <span className="admin-detail-label">Customer ID</span>
                    <span className="admin-detail-value">{selectedOrder.userId || 'Guest'}</span>
                  </div>
                  {getOldProductFromOrder(selectedOrder) && (
                    <div className="admin-detail-row" style={{ marginTop: '10px', background: '#fff7ed', padding: '10px 12px', borderRadius: '8px', borderLeft: '4px solid #f97316' }}>
                      <span className="admin-detail-label" style={{ color: '#c2410c', fontWeight: 'bold' }}>Produkti i vjetër</span>
                      <span className="admin-detail-value" style={{ color: '#9a3412', fontWeight: 'bold', fontSize: '13px' }}>
                        ♻️ {getOldProductFromOrder(selectedOrder)}
                      </span>
                    </div>
                  )}
                </div>

                <div className="admin-detail-card">
                  <div className="admin-modal-section-title">Shipping & Billing</div>
                  <div className="admin-detail-row">
                    <span className="admin-detail-label">Country</span>
                    <span className="admin-detail-value">{selectedOrder.country}</span>
                  </div>
                  {selectedOrder.pickupMethod === 'store' && selectedOrder.shopLocation ? (
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">Tërheqje në Dyqan</span>
                      <span className="admin-detail-value">{selectedOrder.shopLocation.name}</span>
                    </div>
                  ) : (
                    <>
                      <div className="admin-detail-row">
                        <span className="admin-detail-label">Location</span>
                        <span className="admin-detail-value">{selectedOrder.city}, {selectedOrder.region}</span>
                      </div>
                      <div className="admin-detail-row">
                        <span className="admin-detail-label">Address</span>
                        <span className="admin-detail-value">{getCleanAddress(selectedOrder.address)}</span>
                      </div>
                    </>
                  )}
                  <div className="admin-detail-row">
                    <span className="admin-detail-label">Pickup Method</span>
                    <span className="admin-detail-value">{selectedOrder.pickupMethod === 'store' ? 'Në dyqan' : 'Në adresë'}</span>
                  </div>
                  <div className="admin-detail-row">
                    <span className="admin-detail-label">Payment Method</span>
                    <span className="admin-detail-value">{String(selectedOrder.paymentMethod).toUpperCase()}</span>
                  </div>
                </div>
              </div>

              <div className="admin-modal-section" style={{ marginTop: 24 }}>
                <div className="admin-modal-section-title">Notes</div>
                <textarea
                  style={{ width: '100%', minHeight: '80px', padding: '12px', resize: 'vertical', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px', color: '#1e293b' }}
                  placeholder="Shto një shënim për këtë porosi..."
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                />
              </div>

              <div className="admin-modal-section" style={{ marginTop: 24 }}>
                <div className="admin-modal-section-title">Order Items</div>
                <div className="admin-table-container">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th style={{ textAlign: 'right' }}>Price</th>
                        <th style={{ textAlign: 'center' }}>Qty</th>
                        <th style={{ textAlign: 'right' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOrder.items && selectedOrder.items.map((item, idx) => {
                        const itemDisc = getItemDiscountPct(item, selectedOrder);
                        const rawPrice = getItemBasePrice(item);
                        const origPrice = getItemOriginalPrice(item);
                        const finalPrice = itemDisc > 0 ? rawPrice * (1 - itemDisc / 100) : rawPrice;
                        const qty = Number(item.quantity || 1);

                        const displayStrikePrice = origPrice || (itemDisc > 0 ? rawPrice : null);
                        const displayFinalPrice = finalPrice;
                        const hasDiscountOrOffer = Boolean(displayStrikePrice && displayStrikePrice > displayFinalPrice);

                        const strikeTotal = displayStrikePrice ? displayStrikePrice * qty : null;
                        const finalTotal = displayFinalPrice * qty;

                        return (
                          <tr key={idx}>
                            <td style={{ fontWeight: 500 }}>
                              {item.product ? item.product.name : 'Unknown Product'}
                              {item.hasTradeIn && (
                                <div style={{ fontSize: '11.5px', color: '#15803d', marginTop: '4px', fontWeight: 700 }}>
                                  ♻️ Me dorëzim pajisjeje të vjetër (Fushata "Sill të vjetrën merr të renë")
                                </div>
                              )}
                              {item.isBundle && item.bundleItems && item.bundleItems.length ? (
                                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px', fontWeight: 'normal', paddingLeft: '8px', borderLeft: '2px solid #cbd5e1' }}>
                                  <strong>Artikujt e paketës:</strong>
                                  <ul style={{ margin: '2px 0 0 12px', padding: 0, listStyleType: 'disc' }}>
                                    {item.bundleItems.map((c, ci) => (
                                      <li key={ci}>{c.name} {c.side === 'left' ? '(Kryesor)' : '(Shtesë)'}</li>
                                    ))}
                                  </ul>
                                </div>
                              ) : null}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              {hasDiscountOrOffer ? (
                                <div>
                                  <div style={{ textDecoration: 'line-through', color: '#94a3b8', fontSize: 12 }}>
                                    {formatOrderPrice(displayStrikePrice, selectedOrder)}
                                  </div>
                                  <div style={{ color: itemDisc > 0 ? '#166534' : '#0369a1', fontWeight: 700 }}>
                                    {formatOrderPrice(displayFinalPrice, selectedOrder)}{' '}
                                    {itemDisc > 0 ? (
                                      <span style={{ fontSize: 11, background: '#dcfce7', color: '#166534', padding: '2px 6px', borderRadius: 10, fontWeight: 600 }}>
                                        −{itemDisc}% ♻️
                                      </span>
                                    ) : (
                                      <span style={{ fontSize: 11, background: '#e0f2fe', color: '#0369a1', padding: '2px 6px', borderRadius: 10, fontWeight: 600 }}>
                                        Ofertë
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                formatOrderPrice(displayFinalPrice, selectedOrder)
                              )}
                            </td>
                            <td style={{ textAlign: 'center' }}>{qty}</td>
                            <td style={{ textAlign: 'right', fontWeight: 600 }}>
                              {hasDiscountOrOffer ? (
                                <div>
                                  <div style={{ textDecoration: 'line-through', color: '#94a3b8', fontSize: 12 }}>
                                    {formatOrderPrice(strikeTotal, selectedOrder)}
                                  </div>
                                  <div style={{ color: itemDisc > 0 ? '#166534' : '#0369a1', fontWeight: 700 }}>
                                    {formatOrderPrice(finalTotal, selectedOrder)}
                                  </div>
                                </div>
                              ) : (
                                formatOrderPrice(finalTotal, selectedOrder)
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      {(() => {
                        const isOTNOrder = Boolean(getOldProductFromOrder(selectedOrder));
                        const storedPct = getOtnDiscountFromOrder(selectedOrder);

                        const rawSubtotal = (selectedOrder.items || []).reduce((s, it) =>
                          s + getItemBasePrice(it) * Number(it.quantity || 1), 0
                        );

                        const discountedSubtotal = (selectedOrder.items || []).reduce((s, it) => {
                          const d = getItemDiscountPct(it, selectedOrder);
                          const p = getItemBasePrice(it) * (1 - d / 100);
                          return s + p * Number(it.quantity || 1);
                        }, 0);

                        const hasDiscount = isOTNOrder && storedPct > 0 && rawSubtotal > discountedSubtotal + 1;

                        if (!hasDiscount) {
                          return (
                            <tr style={{ background: '#f8fafc' }}>
                              <td colSpan="3" style={{ textAlign: 'right', fontWeight: 700, padding: '16px' }}>Total Amount:</td>
                              <td style={{ textAlign: 'right', fontWeight: 800, fontSize: 16, color: '#0f172a', padding: '16px' }}>
                                {formatOrderPrice(selectedOrder.total, selectedOrder)}
                              </td>
                            </tr>
                          );
                        }

                        return (
                          <>
                            <tr style={{ background: '#fefce8' }}>
                              <td colSpan="3" style={{ textAlign: 'right', fontWeight: 600, padding: '10px 16px', color: '#78716c', fontSize: 13 }}>Subtotal (para zbritjes):</td>
                              <td style={{ textAlign: 'right', fontWeight: 600, fontSize: 13, color: '#78716c', textDecoration: 'line-through', padding: '10px 16px' }}>
                                {formatOrderPrice(rawSubtotal, selectedOrder)}
                              </td>
                            </tr>
                            <tr style={{ background: '#f0fdf4' }}>
                              <td colSpan="3" style={{ textAlign: 'right', fontWeight: 700, padding: '16px' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{ fontSize: 16 }}>♻️</span>
                                  <span style={{ color: '#166534' }}>Total me zbritje ({storedPct}%):</span>
                                </span>
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 800, fontSize: 16, color: '#166534', padding: '16px' }}>
                                {formatOrderPrice(discountedSubtotal, selectedOrder)}
                              </td>
                            </tr>
                          </>
                        );
                      })()}
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>

            <div className="admin-modal-footer">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span className="admin-detail-label">Current Status:</span>
                <span className={`status-badge status-${String(selectedOrder.status).toLowerCase()}`}>
                  {selectedOrder.status}
                </span>
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                {selectedOrder.email ? (
                  <button className="admin-btn admin-btn-secondary" onClick={openHistory}>Order History</button>
                ) : null}
                <select
                  className="admin-select"
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                  style={{ width: 140 }}
                >
                  {statusOptions.map(opt => (
                    <option key={opt} value={opt}>
                      {opt === 'awaiting_payment' ? 'Awaiting Payment' : opt.charAt(0).toUpperCase() + opt.slice(1)}
                    </option>
                  ))}
                </select>
                <button className="admin-btn admin-btn-primary" onClick={saveStatusInModal}>
                  Save Changes
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

      {historyUser && (
        <div className="admin-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) closeHistory(); }}>
          <div className="admin-modal-container" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header" style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
              <h2 className="admin-modal-title">Order History - {historyUser.firstName} {historyUser.lastName}</h2>
              <button className="admin-modal-close" onClick={closeHistory}>✕</button>
            </div>
            <div className="admin-modal-body">
              {historyLoading ? (
                <div className="admin-text">Loading...</div>
              ) : historyOrders.length === 0 ? (
                <div className="admin-text">No orders found</div>
              ) : (
                <div className="admin-table-container">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th style={{ width: '200px' }}>Products</th>
                        <th>Status</th>
                        <th>Payment</th>
                        <th>Total</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyOrders.map((o) => (
                        <tr key={o.id}>
                          <td>{o.id}</td>
                          <td style={{ fontSize: 13, maxWidth: '200px', overflow: 'hidden' }}>
                            {(o.items || []).map((it, idx) => (
                              <div key={idx} style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                {it.product?.name || it.productName || 'Unknown'} x{it.quantity}
                              </div>
                            ))}
                          </td>
                          <td>
                            <span className={`status-badge status-${String(o.status).toLowerCase()}`}>
                              {o.status}
                            </span>
                          </td>
                          <td style={{ fontSize: 13, textTransform: 'uppercase' }}>{o.paymentMethod}</td>
                          <td>{formatOrderPrice(o.total, o)}</td>
                          <td>{o.createdAt ? new Date(o.createdAt).toLocaleString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: '#f8fafc' }}>
                        <td colSpan="4" style={{ textAlign: 'right', fontWeight: 700, padding: '12px' }}>Total Processed Purchases:</td>
                        <td colSpan="2" style={{ textAlign: 'left', fontWeight: 800, color: '#0f172a', padding: '12px' }}>
                          {formatOrderPrice(
                            historyOrders
                              .filter(o => ['delivered', 'processing'].includes(String(o.status).toLowerCase()))
                              .reduce((sum, o) => sum + Number(o.total || 0), 0),
                            historyUser || {}
                          )}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
